import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  PmDialogComponent,
  PmTabsGroupComponent,
  SelectTabOption,
} from '@libs/ui';
import { TranslateModule } from '@ngx-translate/core';
import { AnalysisExportService } from 'demand-forecasting/analysis/services/analysis-export.service';
import { AnalysisMutationsService } from 'demand-forecasting/analysis/services/analysis-mutations.service';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import { map, tap } from 'rxjs';
import { ExportListTabComponent } from './export-list-tab/export-list-tab.component';

export enum ExportTab {
  NEW = 'new',
  LIST = 'list',
}

export interface AnalysisExportDialogData {
  initialTab?: ExportTab;
  highlightExportId?: string;
}

@Component({
  templateUrl: './analysis-export-dialog.component.html',
  imports: [
    PmDialogComponent,
    PmTabsGroupComponent,
    ExportListTabComponent,
    TranslateModule,
  ],
})
export class AnalysisExportDialogComponent implements OnInit {
  protected readonly dialogRef = inject(
    MatDialogRef<AnalysisExportDialogComponent>,
  );
  protected readonly dialogData = inject<AnalysisExportDialogData | null>(
    MAT_DIALOG_DATA,
    { optional: true },
  );
  private readonly analysisExportService = inject(AnalysisExportService);
  private readonly analysisMutationsService = inject(AnalysisMutationsService);
  private readonly analysisStateService = inject(AnalysisStateService);

  protected readonly ExportTab = ExportTab;

  protected activeTab = signal<ExportTab>(
    this.dialogData?.initialTab ?? ExportTab.NEW,
  );

  protected readonly tabOptions: SelectTabOption[] = [
    { value: ExportTab.NEW, label: 'ANALYSIS.EXPORT.TAB_NEW', disabled: false },
    {
      value: ExportTab.LIST,
      label: 'ANALYSIS.EXPORT.TAB_LIST',
      disabled: false,
    },
  ];

  protected selectedExportId = signal<string | undefined>(
    this.dialogData?.highlightExportId,
  );

  protected primaryCtaText = computed(() => {
    switch (this.activeTab()) {
      case ExportTab.LIST:
        return 'COMMON.DOWNLOAD';
      case ExportTab.NEW:
        return 'ANALYSIS.EXPORT.START';
    }
  });

  protected primaryCtaDisabled = computed(() => {
    if (this.activeTab() === ExportTab.LIST) {
      // disable primary cta when no export is selected
      const selected = this.selectedExportId();
      return !selected;
    }
    return this.analysisMutationsService.generateExportLoading();
  });

  protected readonly primaryCtaButtonConfig = computed(() => ({
    text: this.primaryCtaText(),
    disabled: this.primaryCtaDisabled(),
    handleClick: () => this.handlePrimaryCtaClick(),
  }));

  ngOnInit(): void {
    this.activeTab.set(this.dialogData?.initialTab ?? ExportTab.NEW);
  }

  protected handlePrimaryCtaClick() {
    const currentTab = this.activeTab();
    // download selected export
    if (currentTab === ExportTab.LIST) {
      const selectedExportId = this.selectedExportId();
      if (!selectedExportId) {
        console.error('No export selected when trying to download export');
        return;
      }
      this.analysisExportService
        .downloadSelectedExport(selectedExportId)
        .then(() => this.dialogRef.close());
    }

    // start new export
    if (currentTab === ExportTab.NEW) {
      const selectedVersion = this.analysisStateService.selectedVersion();
      if (!selectedVersion) {
        console.error(
          'No analysis version selected when trying to start export',
        );
        return;
      }
      this.analysisExportService
        .startExport(selectedVersion.id)
        .pipe(
          // close dialog after export is started
          tap(() => this.dialogRef.close()),
          map(() => this.analysisStateService.exports.reload()),
        )
        .subscribe();
    }
  }

  protected setActiveTab(tab: string) {
    this.activeTab.set(tab as ExportTab); // since pm-tabs-group does not support generics yet, we need to cast here
  }
}
